import { ConnectedPosition, Overlay, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { OiqLayoutNavItemModel } from './nav-item.models';
import { OiqLayoutNavPath } from './nav.models';

@Component({
  selector: 'oiq-layout-nav-item',
  imports: [CommonModule, MatIconModule, RouterModule, OverlayModule, MatTooltipModule],
  host: {
    class: 'oiq-layout-nav-item',
  },
  templateUrl: './nav-item.component.html',
  styleUrls: ['./nav-item.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OiqLayoutNavItemComponent {
  @HostBinding('class.withSeparator') get classWithSeparator(): boolean {
    return !!this.item.withSeparator;
  }
  @HostBinding('class.isHovering') get classIsHovering(): boolean {
    return this.isHovering;
  }
  @HostBinding('class.hasChildren') get classHasChildren(): boolean {
    return this.visibleChildren.length > 0;
  }

  @HostBinding('class.isSubmenuOpened') get classIsSubmenuOpened(): boolean {
    if (!this.isSidebarExpanded || this.visibleChildren.length == 0) {
      return false;
    }
    return this.isSubmenuOpened;
  }
  @HostBinding('class.isExpanded') get classIsExpanded(): boolean {
    return this.isSidebarExpanded;
  }
  @HostBinding('class.isCollapsed') get classIsCollapsed(): boolean {
    return !this.isSidebarExpanded;
  }
  @HostBinding('class.isSelected') get classIsSelected(): boolean {
    return this.isSelected;
  }
  @HostListener('mouseenter', ['$event']) handleMouseEnter($event): void {
    this.isHovering = true;
    if (this.isSidebarExpanded) {
      return;
    }
    this.openOverlay();
  }
  @HostListener('mouseleave', ['$event']) handleMouseLeave($event): void {
    this.isHovering = false;
    this.closeOverlay();
  }

  @ViewChild('childrenRef')
  childrenRef: TemplateRef<any>;

  @ViewChild('panelRef')
  panelRef: ElementRef<any>;

  /** State of the parent sidebar component */
  @Input() isSidebarExpanded: boolean = false;

  private _item: OiqLayoutNavItemModel | null = null;
  @Input()
  set item(v: OiqLayoutNavItemModel) {
    this._item = v;
    this.visibleChildren = (this._item.children || []).filter(_item => _item.visible !== false);
  }

  get item(): OiqLayoutNavItemModel {
    return this._item;
  }

  private _siblings: OiqLayoutNavItemModel[] = [];
  @Input()
  set siblings(v: OiqLayoutNavItemModel[]) {
    if (JSON.stringify(this._siblings) == JSON.stringify(v)) {
      return;
    }
    this._siblings = v;
    this._processState();
  }

  get siblings(): OiqLayoutNavItemModel[] {
    return this._siblings;
  }

  isSubmenuOpened: boolean = false;

  /** external “selected” flag */
  isSelected = false;

  private _path: OiqLayoutNavPath | null = null;
  public get path(): OiqLayoutNavPath | null {
    return this._path;
  }
  @Input() public set path(v: OiqLayoutNavPath | null) {
    if (JSON.stringify(this._path) == JSON.stringify(v)) {
      return;
    }
    this._path = v;
    this._processState();
  }

  /** current src bound into the template */
  currentSrc!: string;

  isHovering: boolean = false;

  visibleChildren: OiqLayoutNavItemModel[] = [];

  private overlayRef: OverlayRef | null = null;
  private closeTimeout: any;

  constructor(
    private overlay: Overlay,
    private vcr: ViewContainerRef
  ) {}

  toggleSubmenu() {
    this.isSubmenuOpened = !this.isSubmenuOpened;
  }

  openOverlay() {
    if (!this.visibleChildren.length) {
      return;
    }
    if (this.isSidebarExpanded) {
      this.toggleSubmenu();
      return;
    }

    this.cancelClose();

    if (this.overlayRef) {
      return;
    }

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.panelRef)
      .withPositions([
        {
          originX: 'end',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'top',
          offsetX: 32,
        } as ConnectedPosition,
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: false,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });

    const portal = new TemplatePortal(this.childrenRef, this.vcr);
    this.overlayRef.attach(portal);
  }

  closeOverlay() {
    this.cancelClose();
    this.closeTimeout = setTimeout(() => {
      if (!this.overlayRef) {
        return;
      }
      this.overlayRef.detach();
      this.overlayRef.dispose();
      this.overlayRef = null;
    }, 200); // Short delay to allow mouseenter in the popover
  }

  cancelClose() {
    clearTimeout(this.closeTimeout);
  }

  private _processState(): void {
    if (!this._path || !this.item) {
      this.isSubmenuOpened = false;
      this.isSelected = false;
      return;
    }

    // check for children to be opened
    const _exactSiblingPath = (this.siblings || []).find(
      _child => _child.route == this.path.url && _child.route !== this.item.route
    );

    this.isSubmenuOpened = this.visibleChildren.some(_child =>
      this.path.url.startsWith(_child.route)
    );

    if (_exactSiblingPath) {
      this.isSelected = false;
      return;
    }

    const _normalizedAlias: string[] = (
      this.item.routeAlias && !Array.isArray(this.item.routeAlias)
        ? [this.item.routeAlias]
        : this.item.routeAlias || []
    ) as string[];

    if (
      _normalizedAlias.length &&
      _normalizedAlias.find(_alias => this.path.url.startsWith(_alias))
    ) {
      this.isSubmenuOpened = true;
      this.isSelected = true;
      return;
    }

    // If the item defines queryParams (e.g. label id), require them to match
    if (!!(this.item && this.item.queryParams && Object.keys(this.item.queryParams).length)) {
      const urlParts = (this.path.url || '').split('?');
      const queryString = urlParts[1] || '';
      const params = new URLSearchParams(queryString);
      const queryMatches = Object.entries(this.item.queryParams).every(
        ([k, v]) => params.get(k) === String(v)
      );

      this.isSelected = this.path.url.startsWith(this.item.route) && queryMatches;
      return;
    }

    this.isSelected = this.isSubmenuOpened || this.path.url.startsWith(this.item.route);
  }

  trackByItem(index: number, item: any): any {
    return JSON.stringify(item);
  }
}
